import { CheckCircle2, Circle, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';

export type SyncStep = 'groups' | 'contacts' | 'groupData';

export interface SyncProgress {
  currentStep: SyncStep;
  completedSteps: SyncStep[];
  totalSteps: number;
}

interface SyncProgressModalProps {
  isOpen: boolean;
  progress: SyncProgress;
}

const STEP_LABELS: Record<SyncStep, string> = {
  groups: 'Fetching available groups',
  contacts: 'Syncing contacts',
  groupData: 'Syncing group data',
};

const STEP_ORDER: SyncStep[] = ['groups', 'contacts', 'groupData'];

export const SyncProgressModal = ({
  isOpen,
  progress,
}: SyncProgressModalProps) => {
  const progressPercentage = Math.round(
    (progress.completedSteps.length / progress.totalSteps) * 100,
  );

  const getStepIcon = (step: SyncStep) => {
    if (progress.completedSteps.includes(step)) {
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    } else if (progress.currentStep === step) {
      return <Loader2 className="h-5 w-5 animate-spin text-blue-600" />;
    } else {
      return <Circle className="h-5 w-5 text-gray-300" />;
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="sm:max-w-md"
        // Prevent closing during sync
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            Setting Up Your Workspace
          </DialogTitle>
          <DialogDescription className="text-center">
            Please wait while we sync your data...
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Steps */}
          <div className="space-y-3">
            {STEP_ORDER.map((step) => (
              <div key={step} className="flex items-center gap-3">
                {getStepIcon(step)}
                <span
                  className={`text-sm ${
                    progress.completedSteps.includes(step)
                      ? 'font-medium text-green-700'
                      : progress.currentStep === step
                        ? 'font-medium text-blue-700'
                        : 'text-gray-500'
                  }`}
                >
                  {STEP_LABELS[step]}
                </span>
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <Progress value={progressPercentage} className="h-2" />
            <p className="text-center text-xs text-gray-500">
              {progressPercentage}% complete
            </p>
          </div>

          {/* Status Message */}
          <p className="text-center text-sm text-gray-600">
            This may take a few seconds...
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
